import React, { useEffect, useState } from 'react';
import { Check, Copy, Inbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { wget } from './api';

const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.error('Copy failed — select and copy manually'); } };

export const formSnippet = (endpoint, thanks) => `<form action="${endpoint}" method="POST">
  <input type="hidden" name="redirect" value="${thanks}">
  <input type="text" name="company_website" style="display:none" tabindex="-1" autocomplete="off">
  <input name="name" placeholder="Your name" required>
  <input name="phone" type="tel" placeholder="Phone">
  <input name="email" type="email" placeholder="Email">
  <input name="postcode" placeholder="Postcode">
  <select name="service">
    <option>Resin Bound Driveway</option>
    <option>Epoxy Flooring</option>
    <option>Decorative Walling</option>
    <option>Other</option>
  </select>
  <textarea name="message" placeholder="Tell us about your project"></textarea>
  <button type="submit">Send enquiry</button>
</form>`;

export const fetchSnippet = (endpoint) => `fetch("${endpoint}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ name, phone, email, postcode, service, message })
}).then(r => r.json()).then(d => { if (d.ok) showThanks(d.ref); });`;

function Code({ text, label, testId }) {
  const [done, setDone] = useState(false);
  return (
    <div className="relative min-w-0 w-full overflow-hidden rounded-lg">
      <pre className="text-[11px] leading-snug max-w-full bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto whitespace-pre" data-testid={testId}>{text}</pre>
      <button type="button" onClick={() => { copy(text, label); setDone(true); setTimeout(() => setDone(false), 1500); }} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded" data-testid={`${testId}-copy`}>{done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {done ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

/** Staff panel: how to wire the trainee's website contact form into their ProPortal leads. */
export function EnquiryIntegration({ order, staff = true }) {
  const [d, setD] = useState(null);
  useEffect(() => { wget(`${staff ? '/staff' : ''}/website/orders/${order.id}/enquiry-integration`).then(setD).catch((e) => toast.error(e.message)); }, [order.id, staff]);
  if (!d) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
  const live = (order.admin?.live_url || 'https://your-domain.co.uk').replace(/\/$/, '');
  const thanks = `${live}/thanks.html`;
  return (
    <div className="space-y-3 min-w-0" data-testid="wod-enquiry-integration">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800"><Inbox className="w-4 h-4 text-teal-700" /> {d.leads_received} enquir{d.leads_received === 1 ? 'y' : 'ies'} received</span>
        {d.last_lead && <span className="text-xs text-gray-500">last: {d.last_lead.customer_name} · {new Date(d.last_lead.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
      </div>
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1">Enquiry endpoint (unique to this trainee)</div>
        <div className="flex gap-2"><code className="flex-1 text-[11px] bg-gray-100 rounded-md px-2 py-1.5 break-all" data-testid="wod-enquiry-endpoint">{d.endpoint}</code><Button type="button" size="sm" variant="outline" className="h-8" onClick={() => copy(d.endpoint, 'Endpoint')} data-testid="wod-enquiry-copy-endpoint"><Copy className="w-3.5 h-3.5" /></Button></div>
      </div>
      <p className="text-xs text-gray-500">Point the website's contact form at this URL — either a plain HTML form (works on any static template, redirects to a thank-you page) or a JavaScript fetch. Accepted fields: <code>name, phone, email, postcode, service, message, redirect</code>. Keep the hidden <code>company_website</code> field empty — it's the spam trap. The service text is used to file the lead under ProBound / ProEpoxy / ProDecor automatically.</p>
      <div className="grid grid-cols-1 gap-3 min-w-0">
        <div className="min-w-0"><div className="text-xs font-semibold text-gray-600 mb-1">Option A — HTML form (recommended for the stock templates)</div><Code text={formSnippet(d.endpoint, thanks)} label="HTML form" testId="wod-enquiry-html" /></div>
        <div className="min-w-0"><div className="text-xs font-semibold text-gray-600 mb-1">Option B — JavaScript fetch (JSON)</div><Code text={fetchSnippet(d.endpoint)} label="JavaScript" testId="wod-enquiry-js" /></div>
      </div>
    </div>
  );
}
